import axios, { CancelTokenSource } from 'axios';
import { ApiClientReturnType, KeyLabelType, Nullable } from '~/types';
import api from '../../api/http-common'
import ENV from '~/environment'

// Registrations

const getClimaRegistrationShow = (id: string) =>
  api.post(`/climate-program/registrations/${id}/show`)

const getClimaRegistrationsIndex = (data?) =>
  api.post('/climate-program/registrations/index', data)

const getClimaRegOptionsData = (): ApiClientReturnType<ClimaRegDataType> =>
  api.post('/climate-program/register/get-options-data')

const getAcceptTermsFile = (): ApiClientReturnType<PurchasOptionsType> =>
  api.post('/settings/climate-program/get-file')

const registerClimaProgram = ({ formData, onUploadProgress, cancelToken }: RegisterOptions) =>
  axios.post(`${ENV.API_URL}/climate-program/register`, formData, {
    headers: {
      'Authorization': localStorage.getItem('token'),
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress,
    cancelToken: cancelToken?.token
  })

const getClimaRegistrationFile = (id: string) =>
  api.post(`/climate-program/registrations/${id}/get-file`)

// Purchases

const getPurchasesOptionsData = () =>
  api.post('/climate-program/purchases/get-options-data')

const createPurchase = ({ formData, onUploadProgress, cancelToken }: RegisterOptions) =>
  axios.post(`${ENV.API_URL}/climate-program/purchases/store`, formData, {
    headers: {
      'Authorization': localStorage.getItem('token'),
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress,
    cancelToken: cancelToken?.token
  })

const updatePurchase = ({ id, formData, onUploadProgress, cancelToken }: UpdatePurchaseOptions) =>
  axios.post(`${ENV.API_URL}/climate-program/purchases/${id}/update`, formData, {
    headers: {
      'Authorization': localStorage.getItem('token'),
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress,
    cancelToken: cancelToken?.token
  })

const getPurchases = (data) =>
  api.post('/climate-program/purchases/index', data)

const deletePurchase = (id: number) =>
  api.post(`/climate-program/purchases/${id}/delete`)

const editPurchase = (id: string) =>
  api.post(`/climate-program/purchases/${id}/edit`)

const getClimaPurchaseFile = (id: string) =>
  api.post(`/climate-program/purchases/${id}/get-file`)

const showPurchase = (id): ApiClientReturnType<FeedPurchaseShowType> =>
  api.post(`/climate-program/purchases/${id}/show`);

const getPurchasesIndexData = () =>
  api.post('/climate-program/purchases/get-index-data');

export const ClimaProgramService = {
  getClimaRegOptionsData,
  getAcceptTermsFile,
  registerClimaProgram,
  getPurchasesOptionsData,
  createPurchase,
  getPurchases,
  updatePurchase,
  deletePurchase,
  editPurchase,
  getClimaPurchaseFile,
  getClimaRegistrationFile,
  showPurchase,
  getPurchasesIndexData,
  getClimaRegistrationsIndex,
  getClimaRegistrationShow
}

interface RegisterOptions {
  formData: FormData
  onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
  cancelToken?: CancelTokenSource
}

interface UpdatePurchaseOptions extends RegisterOptions {
  id?: string
}
export interface ClimaRegDataType {
  milkAmounts: Amount[];
  concentratedFeedsAmounts: Amount[];
  farmers: ClimaRegFarmerType[];
  email: string
}

export interface Amount {
  key: number;
  value: number;
}

export interface ClimaRegFarmerType {
  id: string;
  companyName: string;
  agisId: null;
  phoneNumber: null;
  addressableId: string;
  streetName: string;
  streetNumber: string;
  zip: string;
  climateProgramRegistration: Nullable<ClimaRegistrationDataType>
  city: string;
}

export interface ClimaRegistrationDataType {
  id: number;
  farmerId: string;
  year: number;
  companyName: string;
  agisId: string;
  streetName: string;
  streetNumber: string;
  zip: string;
  city: string;
  phoneNumber: null;
  email: null;
  memberSince: null;
  cowsNumber: number;
  avgMilkAmount: number;
  yearlyConcentratedFeedAmountPerCow: number;
  changesInLastThreeYears: number;
  selfCalculatedFeedingPlan: number;
  consultantFirstName: string;
  consultantLastName: string;
  consultantPhoneNumber: string;
  consultantEmail: string;
  consultantCompanyName: string;
  feedingPlanOriginalFilename: null;
  feedingPlanFilename: null;
  createdAt: Date;
  updatedAt: Date;
  changes: any[];
}

export interface PurchasOptionsType {
  animalGroups: {key: number, label: string}[];
  feedSuppliers: FeedSupplier[];
}

export interface AnimalGroup {
  id: number;
  climateProgramPurchaseId: number;
  group: number;
  avgCowsNumberFedWithAgoline: number;
  dailyMineralIntakePerCow: number;
}

export interface FeedSupplier {
  id: number;
  status: number;
  code: string;
  icon: string;
  name: string;
  label: string;
  feedSupplierId: number;
  languageId: number;
  products: Product[];
}

export interface Product {
  id: number;
  feedSupplierId: number;
  agolineContent: string;
  name: string;
  status: number;
}
export interface FeedPurchaseShowType {
  evsNumber: string;
  companyName: string;
  id: number;
  farmerId: string;
  agisId: string;
  year: number;
  status: number;
  animalGroup: number;
  purchaseDate: Date;
  feedSupplierId: number;
  customerMix: number;
  feedProductId: null;
  agolineContent: string;
  purchasedFeedAmount: number;
  feedingFrom: Date;
  feedingTo: Date;
  purchaseReceiptOriginalFilename: string;
  purchaseReceiptFilename: string;
  createdAt: Date;
  updatedAt: Date;
  feedSupplierName: string;
  feedProductName: string;
  agolineAmount: string;
  dairyCowsDailyAgolineAmountPerCow: string;
  dryCowsDailyAgolineAmountPerCow: string;
  sumAnimalGroupsFeedAmount: string;
  climateProgramPurchaseId: number;
  dairyCowsDailyMineralIntakePerCow: string;
  dryCowsDailyMineralIntakePerCow: string;
  dairyCowsNumber: string;
  dryCowsNumber: string;
  statusLabel: string;
  animalGroupLabel: string;
}
